/**
 * Payment Policy Page - Chính sách thanh toán
 */

import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../../utils/siteConfig';
import { StructuredData } from '../../components/StructuredData';
import { CreditCard, Shield, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export function PaymentPolicyPage() {
  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Chính sách thanh toán | Hướng nghiệp CAT'
        description='Chính sách thanh toán và các phương thức thanh toán được chấp nhận tại Hướng nghiệp CAT'
        canonicalUrl={getCanonicalUrl('/chinh-sach-thanh-toan')}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Chính sách thanh toán', url: getCanonicalUrl('/chinh-sach-thanh-toan') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Header */}
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
              Chính sách thanh toán
            </h1>
            <p className='text-lg text-gray-600 dark:text-gray-300'>
              Cập nhật lần cuối: Tháng 12/2024
            </p>
          </div>

          <div className='space-y-8'>
            {/* Payment Methods */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center'>
                  <CreditCard className='w-6 h-6 text-green-600 dark:text-green-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Phương thức thanh toán
                </h2>
              </div>

              <p className='text-gray-600 dark:text-gray-300 mb-4'>
                Chúng tôi chấp nhận các phương thức thanh toán sau:
              </p>

              <ul className='space-y-3'>
                {['Chuyển khoản ngân hàng', 'Quét mã QR Code (VietQR)'].map((method, index) => (
                  <li key={index} className='flex items-center gap-3'>
                    <CheckCircle className='w-5 h-5 text-green-500 flex-shrink-0' />
                    <span className='text-gray-600 dark:text-gray-300'>{method}</span>
                  </li>
                ))}
              </ul>

              {/* Pricing Transparency Notice */}
              <div className='mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600'>
                <p className='text-sm text-gray-700 dark:text-gray-300'>
                  Giá dịch vụ được công bố công khai trên website và có thể điều chỉnh theo từng
                  thời điểm.
                </p>
              </div>
            </div>

            {/* Payment Process */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center'>
                  <Clock className='w-6 h-6 text-blue-600 dark:text-blue-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Quy trình thanh toán
                </h2>
              </div>

              <ol className='space-y-4'>
                {[
                  {
                    step: 1,
                    title: 'Chọn sản phẩm',
                    desc: 'Chọn bài thi hoặc gói báo cáo bạn muốn mở khóa',
                  },
                  {
                    step: 2,
                    title: 'Chọn phương thức thanh toán',
                    desc: 'Lựa chọn hình thức thanh toán phù hợp',
                  },
                  {
                    step: 3,
                    title: 'Xác nhận thanh toán',
                    desc: 'Hoàn tất thanh toán theo hướng dẫn',
                  },
                  {
                    step: 4,
                    title: 'Nhận quyền truy cập',
                    desc: 'Quyền truy cập được cấp ngay sau khi thanh toán thành công',
                  },
                ].map(({ step, title, desc }) => (
                  <li key={step} className='flex gap-4'>
                    <div className='w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0'>
                      {step}
                    </div>
                    <div>
                      <h3 className='font-semibold text-gray-900 dark:text-white'>{title}</h3>
                      <p className='text-gray-600 dark:text-gray-300'>{desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Security */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center'>
                  <Shield className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Bảo mật thanh toán
                </h2>
              </div>

              <div className='prose dark:prose-invert max-w-none'>
                <p className='text-gray-600 dark:text-gray-300 mb-4'>
                  Chúng tôi cam kết bảo mật tuyệt đối thông tin thanh toán của bạn:
                </p>

                <ul className='list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2'>
                  <li>Tất cả giao dịch được mã hóa SSL 256-bit</li>
                  <li>Không lưu trữ thông tin thẻ tín dụng trên hệ thống</li>
                  <li>Tuân thủ tiêu chuẩn bảo mật PCI DSS</li>
                  <li>Thanh toán qua cổng được cấp phép bởi Ngân hàng Nhà nước</li>
                </ul>
              </div>
            </div>

            {/* Notice for THPT Students */}
            <div className='bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800'>
              <h3 className='font-semibold text-blue-800 dark:text-blue-300 mb-2'>
                Lưu ý dành cho học sinh THPT
              </h3>
              <p className='text-blue-700 dark:text-blue-400'>
                Việc thanh toán có thể được thực hiện bởi phụ huynh hoặc người giám hộ hợp pháp.
              </p>
            </div>

            {/* BIDV Account Opening Guide */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8'>
              <div className='mb-6'>
                <p className='text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-base sm:text-lg'>
                  Nếu quý vị chưa có tài khoản thanh toán, có thể mở tài khoản theo hướng dẫn sau:
                </p>

                {/* Promotional Notice */}
                <div className='mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800'>
                  <p className='text-sm sm:text-base text-green-800 dark:text-green-300 leading-relaxed'>
                    <strong className='font-semibold'>
                      Học sinh mở tài khoản BIDV từ link dưới đây và sử dụng để thanh toán trên hệ
                      thống Hướng nghiệp CAT sẽ có cơ hội nhận được voucher giảm giá 20.000 sử dụng
                      mở khóa báo cáo hướng nghiệp hoặc giảm giá khi làm các bài thi thử TN THPT
                      trên hệ thống.
                    </strong>
                  </p>
                </div>

                <div className='mt-4'>
                  <a
                    href='https://dl.bidv.com.vn/MxnMIXTrah5'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='block w-full max-w-4xl mx-auto group cursor-pointer'
                    aria-label='Mở tài khoản BIDV - Nhấn để mở link tạo tài khoản'
                  >
                    <div className='relative overflow-hidden rounded-lg shadow-md border-2 border-gray-200 dark:border-gray-700 group-hover:border-green-500 dark:group-hover:border-green-400 transition-all duration-300'>
                      <img
                        src='/mo-tai-khoan-bidv.png'
                        alt='Hướng dẫn mở tài khoản BIDV - Nhấn để mở link tạo tài khoản'
                        className='w-full h-auto rounded-lg transition-transform duration-300 group-hover:scale-[1.02]'
                      />
                      {/* Mobile tap hint overlay - visible on mobile */}
                      <div className='absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20 sm:from-transparent sm:via-transparent sm:to-transparent flex items-end justify-center pb-3 sm:hidden pointer-events-none'>
                        <div className='bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-semibold shadow-lg'>
                          👆 Nhấn để mở tài khoản BIDV
                        </div>
                      </div>
                    </div>
                    {/* Mobile hint text below image */}
                    <p className='mt-3 text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400 sm:group-hover:text-green-600 sm:dark:group-hover:text-green-400 transition-colors'>
                      <span className='sm:hidden font-semibold text-green-600 dark:text-green-400'>
                        👆 Nhấn vào ảnh trên để mở link tạo tài khoản BIDV
                      </span>
                      <span className='hidden sm:inline'>
                        Nhấn vào ảnh để mở link tạo tài khoản BIDV
                      </span>
                    </p>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className='bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800'>
              <h3 className='font-semibold text-amber-800 dark:text-amber-300 mb-2'>
                Cần hỗ trợ về thanh toán?
              </h3>
              <p className='text-amber-700 dark:text-amber-400'>
                Liên hệ với chúng tôi qua email: <strong>support@huongnghiep.cat.edu.vn</strong>
              </p>
            </div>
          </div>
        </div>
      </motion.main>

      <UnifiedFooter />
    </div>
  );
}

export default PaymentPolicyPage;
